<template>
  <FAQSectionLayout>
    <h3 class="faq-box__subtitle">Shortform App</h3>

    <div class="faq-list">
      <div class="faq-half-list">
        <FAQUnit type="support" title="What devices can I use Shortform on?">
          The Shortform app is available on
          <a
            href="https://apps.apple.com/us/app/shortform-daily-book-summaries/id1489472007"
            target="_blank"
          >
            iOS </a
          >,
          <a
            href="https://play.google.com/store/apps/details?id=com.shortform.app"
            target="_blank"
            >Android</a
          >, and
          <a href="https://www.shortform.com/" target="_blank">Desktop</a>.
        </FAQUnit>
        <FAQUnit type="support" title="How can I request a book summary?">
          Send us a request with the title you'd like to see covered using
          <RouterLink to="/app/help#support" @click="scrollToSupport">
            the form on this page
          </RouterLink>
          or email us at
          <a href="mailto:help@shortform.com">help@shortform.com</a>.
        </FAQUnit>
      </div>
      <div class="faq-half-list">
        <FAQUnit
          type="support"
          title="How can I make the most of my Shortform subscription?"
        >
          Click
          <button class="" @click="toggleChangelogDialog">
            here to open the “what’s new” screen
          </button>
          and find out more about the app features. <br /><br />
          Also check out
          <a
            href="https://www.shortform.com/blog/how-to-use-shortform/"
            target="_blank"
          >
            this article
          </a>
          for an in-depth look at all of Shortform's features.
        </FAQUnit>
        <FAQUnit
          type="support"
          title="Where can I report a technical problem or bug?"
        >
          Please report all bugs to the support team using the
          <RouterLink to="/app/help#support" @click="scrollToSupport">
            Support Request form
          </RouterLink>
          on this page or email us directly at
          <a href="mailto:help@shortform.com">help@shortform.com</a>.
        </FAQUnit>
      </div>
    </div>

    <h3 class="faq-box__subtitle">Account</h3>

    <div class="faq-list">
      <div class="faq-half-list">
        <FAQUnit type="support" title="How do I change my password?">
          You can reset your password by navigating to your account
          <RouterLink to="/app/settings">Settings</RouterLink> and clicking the
          “Change Password” button.
        </FAQUnit>
        <FAQUnit
          type="support"
          title="How can I change the language settings on my account?"
        >
          Shortform is available in English, Español, Deutsch, and Português
          Brasileiro. To change the language that displays on the app for you,
          click the profile icon (top right). Then, click the Language dropdown
          menu and choose a language.
        </FAQUnit>
      </div>
      <div class="faq-half-list">
        <FAQUnit
          type="support"
          title="Can I change the email associated with my account?"
        >
          Yes, you can change your email by navigating to your account
          <RouterLink to="/app/settings">Settings</RouterLink>
          and clicking the “Change Email” button.
        </FAQUnit>
        <FAQUnit
          type="support"
          title="How do I permanently delete my Shortform account?"
        >
          You can delete your account on the
          <RouterLink to="/app/settings">Settings</RouterLink> screen. Your
          account and your data will be permanently deleted. <br /><br />
          Alternatively, you may want to keep your account but cancel your paid
          plan so you have the option to resubscribe to a paid plan at a later
          time without losing your data.
        </FAQUnit>
      </div>
    </div>

    <h3 class="faq-box__subtitle">Free Trial</h3>

    <div class="faq-list">
      <div class="faq-half-list">
        <FAQUnit type="support" title="What happens after my free trial ends?">
          If you do not cancel your free trial before the end date, then the
          card on file will be automatically charged for the subscription plan
          you chose at the start of your trial.
        </FAQUnit>
      </div>
      <div class="faq-half-list">
        <FAQUnit
          type="support"
          title="Can I cancel my Shortform free trial before it ends?"
        >
          <strong>If you started your trial through the web</strong>, you can
          cancel your free trial before the end date from the
          <RouterLink to="/app/billing">Billing</RouterLink> screen. Keep in
          mind that when you cancel during a free trial, you will lose access to
          Shortform content. You can restart the trial at any point before the
          original end date of the trial period. The end date remains the same.
          <br /><br />
          <strong>If you started your trial on the mobile app</strong>, your
          payments are managed through either Apple or Google. You may cancel
          your trial and continue to access Shortform content through your full
          trial period.
        </FAQUnit>
      </div>
    </div>

    <h3 class="faq-box__subtitle">Payment and Subscription</h3>

    <div class="faq-list">
      <div class="faq-half-list">
        <FAQUnit
          type="support"
          title="How can I update my payment information?"
        >
          <strong>If you subscribed on the Shortform website or app:</strong>
          You can change your payment method by tapping “Edit” in the “Payment
          Information” box on the
          <RouterLink to="/app/billing">Billing</RouterLink> screen.
          <br /><br />
          <strong>If you subscribed through Apple App Store:</strong> You can
          manage your payment settings in the AppStore app. <br /><br />
          <strong>If you subscribed through Google Play:</strong> You can manage
          your payment settings in the Google Play app.
        </FAQUnit>
        <FAQUnit
          type="support"
          title="How can I cancel my Shortform subscription?"
        >
          <strong>If you subscribed on the Shortform website or app:</strong
          ><br />
          Visit our <RouterLink to="/app/billing">Billing</RouterLink> page →
          Click “Cancel subscription.”<br /><br />
          <strong>If you subscribed through Apple App Store:</strong><br />
          Use iOS Settings app → Your Name → Subscriptions → Shortform →
          Cancel.<br /><br />
          <strong>If you subscribed through Google Play:</strong><br />
          In the Google Play app → Profile → Payments & Subscriptions →
          Subscriptions → Shortform → Cancel.<br /><br />
        </FAQUnit>
      </div>
      <div class="faq-half-list">
        <FAQUnit
          type="support"
          title="Can I switch from a monthly to an annual subscription?"
        >
          You can change your plan at any time on the
          <RouterLink to="/app/billing">Billing</RouterLink> page.
        </FAQUnit>
        <FAQUnit
          type="support"
          title="If I cancel, will I still have access to Shortform until the end of my subscription?"
        >
          <strong>Yes.</strong> When you cancel, your subscription stays active
          until the end of the billing period you’ve already paid for. You won’t
          lose access immediately.
        </FAQUnit>
      </div>
    </div>

    <div class="support-remark">
      Didn’t find an answer to your question?
      <RouterLink
        to="/app/help#support"
        class="support-remark__link"
        @click="scrollToSupport"
      >
        Fill out the form above
      </RouterLink>
      or send us an email directly at
      <a href="mailto:help@shortform.com">help@shortform.com</a>.
    </div>

    <Teleport to="#appRoot">
      <ChangelogPopup ref="popupChangelog" :view="changelogView!" />
    </Teleport>
  </FAQSectionLayout>
</template>

<script setup lang="ts">
import { onBeforeMount, ref } from 'vue'
import type { Raw, Ref } from 'vue'

import ChangelogPopup from '@/app/components/changelog/Popup.vue'
import { createChangelogView } from '@/models/changelog'
import type { ChangelogView } from '@/models/changelog'
import * as Mixpanel from '@/analytics/mixpanel/mixpanel'
import { assertNotNull } from '@/helpers/typing'
import { scrollIntoView } from '@/helpers/dom'

import FAQSectionLayout from './FAQSectionLayout.vue'
import FAQUnit from './FAQUnit.vue'

const popupChangelog = ref<InstanceType<typeof ChangelogPopup> | null>(null)
const changelogView: Ref<Raw<ChangelogView> | null> = ref(null)

onBeforeMount(async () => {
  changelogView.value = await createChangelogView()
})

async function toggleChangelogDialog(): Promise<void> {
  await changelogView.value?.loadData()
  assertNotNull(popupChangelog.value).show()
  Mixpanel.trackChangelogButtonClick()
}

function scrollToSupport(): void {
  scrollIntoView('#support', { behaviour: 'smooth' })
}
</script>

<style lang="scss" scoped>
.faq-box {
  :has(.mini-player) & {
    padding-bottom: 110px;

    @media (max-width: $w-tablet-s) {
      padding-bottom: 80px;
    }
  }

  &__subtitle {
    display: block;
    text-align: center;
    margin-bottom: 24px;
  }

  a:not(.support-remark__link),
  button {
    color: $c-blue-2;
    text-decoration: underline;
  }

  .support-remark {
    display: block;
    text-align: center;
    color: $c-black-3;

    &__link {
      text-decoration: underline;
      color: $c-black-3;
    }
  }
}
</style>
